import { useMemo, useState } from 'react';
import { Segmented } from 'antd';

import { DataTable } from '../../../../components/shared';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';
import { ghiNho, nho } from '../../hooks/ghi-nho';
import { beRongCot, laCotSo } from '../../nghiep-vu/bieu-do';

/** Khoá nhớ lựa chọn tiêu đề cột — dùng chung mọi bảng kết quả trên máy này. */
const KHOA_TIEU_DE = 'bang.tieuDe';
type KieuTieuDe = 'nhan' | 'ten';

/**
 * Bảng kết quả — hàng dữ liệu thật, khi lượt hỏi CẦN bảng (port `BangKetQua.tsx` native).
 *
 * Máy chủ được thiết kế với giả định có một cái bảng: lời văn bị cấm liệt kê và trên 12 dòng thì không được kể tên nữa
 * — "việc ấy là của BẢNG". Bảng chỉ vẽ khi máy chủ nói lượt này cần (`veBang`): dán một lưới dữ liệu dưới câu "doanh
 * thu tháng này là 4,2 tỷ" là đúng thứ lời văn sinh ra để thay.
 */
export function BangKetQua({
  columns,
  rows,
  nhan,
  className,
}: {
  columns: string[];
  rows: Record<string, unknown>[];
  /** Nhãn cột theo tên cột (`AskResult.nhanCot`); thiếu thì hiện tên cột. */
  nhan?: Record<string, string>;
  className?: string;
}) {
  const { t, locale } = useT();
  const coNhan = columns.some((c) => !!nhan?.[c]);
  // Mặc định hiện NHÃN — cách nghiệp vụ gọi con số. Tên cột thô vẫn một cú bấm, cho người soạn khuôn đối chiếu.
  const [kieu, setKieu] = useState<KieuTieuDe>(() => (nho(KHOA_TIEU_DE) === 'ten' ? 'ten' : 'nhan'));
  const dungNhan = coNhan && kieu === 'nhan';

  const dinhDang = useMemo(
    () =>
      (v: unknown): string => {
        if (v === null || v === undefined || v === '') return '—';
        if (typeof v === 'number') return v.toLocaleString(locale, { maximumFractionDigits: 2 });
        return String(v);
      },
    [locale],
  );

  // ── BỀ RỘNG TỪNG CỘT + CUỘN NGANG ── `ellipsis` buộc `table-layout: fixed`; không đặt bề rộng thì khung chat bị
  // CHIA ĐỀU cho mọi cột — kết quả thủ tục SAP 15–20 cột thành những cột rộng vài chữ.
  const cot = useMemo(
    () =>
      columns.map((c) => {
        const laSo = laCotSo(rows, c);
        const tieuDe = (dungNhan && nhan?.[c]) || c;
        return {
          title: tieuDe,
          dataIndex: c,
          key: c,
          width: beRongCot(tieuDe, rows.slice(0, 50).map((r) => dinhDang(r[c]).length)),
          ellipsis: { showTitle: true },
          align: laSo ? ('end' as const) : undefined,
          className: laSo ? 'tabular-nums' : undefined,
          render: (v: unknown) => dinhDang(v),
        };
      }),
    [columns, rows, nhan, dungNhan, dinhDang],
  );
  const tongRong = useMemo(() => cot.reduce((a, c) => a + c.width, 0), [cot]);
  const viTri = useMemo(() => new Map(rows.map((r, i) => [r, i])), [rows]);
  if (!rows.length || !columns.length) return null;

  return (
    // `min-w-0 max-w-full`: thiếu hai lớp này thì bảng rộng đẩy cả khung chat giãn ra thay vì cuộn bên trong.
    <div className={cn('min-w-0 max-w-full', className)}>
      {coNhan ? (
        <div className="mb-2 flex justify-start">
          <Segmented<KieuTieuDe>
            size="small"
            aria-label={t('troLyAiHoiLuot.kieuTieuDe')}
            value={kieu}
            onChange={(v) => {
              setKieu(v);
              ghiNho(KHOA_TIEU_DE, v);
            }}
            options={[
              { value: 'nhan', label: t('troLyAiHoiLuot.tieuDeNhan') },
              { value: 'ten', label: t('troLyAiHoiLuot.tieuDeTenCot') },
            ]}
          />
        </div>
      ) : null}
      <DataTable<Record<string, unknown>>
        caption={t('troLyAiHoiLuot.bangKetQua')}
        size="small"
        // Hàng là kết quả một câu SQL bất kỳ — có thể không có cột `id`, hoặc `id` trùng khi `GROUP BY`. Dùng CHỈ SỐ.
        rowKey={(r) => String(viTri.get(r) ?? 0)}
        columns={cot}
        dataSource={rows}
        scroll={{ x: tongRong, y: rows.length > 10 ? 700 : undefined }}
        // Phân trang thay vì cuộn vô hạn: 5.000 dòng là 5.000 hàng DOM trong một khung chat. `showTotal` nói ra tổng.
        pagination={{
          pageSize: 20,
          size: 'small',
          showSizeChanger: false,
          hideOnSinglePage: true,
          showTotal: (tong, [dau, cuoi]) =>
            t('troLyAiHoiLuot.phanTrang', { dau: soVN(dau, locale), cuoi: soVN(cuoi, locale), tong: soVN(tong, locale) }),
        }}
      />
    </div>
  );
}
